import {
  DEFAULT_LOCALE,
  LOCALES,
  type CatalogEntry,
  type CatalogEntryData,
  type CatalogFieldDeclaration,
  type Locale,
  type ModuleAccent,
} from '@nexus/core';
import { queryOptions, useQueries, type UseQueryResult } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../api/client';
import { useOfflineRecords } from '../offline/offline';
import { useOnline } from '../sync/sync-status';

/** Offline entity of the kernel: the entries of every catalog the person may read. */
const CATALOG_OFFLINE_ENTITY = 'core.catalogs';

/** A catalog as `GET /api/catalogs` lists it. */
export interface CatalogSummary {
  key: string;
  /** Module ID, `core` for the kernel catalogs. */
  owner: string;
  label: string;
  description: string | null;
  fields: CatalogFieldDeclaration[];
  /** Whether the person may change the entries. */
  canManage: boolean;
  /** Entries of the company, inactive ones included. */
  entries: number;
  /** Entries of the defaults the company can load. */
  defaults: number;
}

/** Fields of a new entry. A change sends only the changed ones, with `version`. */
export interface CatalogEntryInput {
  name: string;
  code: string | null;
  color: ModuleAccent | null;
  description: string | null;
  isDefault: boolean;
  active: boolean;
  data: CatalogEntryData;
}

export interface CatalogImportResult {
  created: number;
  updated: number;
  unchanged: number;
  deactivated: number;
  /** References to names the other catalog does not have; they stay empty. */
  unresolved: number;
  /** New entries left out because a required reference found no entry. */
  skipped: number;
}

export interface CatalogDefaultsResult {
  created: number;
  existing: number;
  unresolved: number;
  skipped: number;
}

const base = (key: string) => `/catalogs/${encodeURIComponent(key)}`;

/** Language of the person, for the defaults of the kernel catalogs. */
export function useLocale(): Locale {
  const { i18n } = useTranslation();
  return (LOCALES as readonly string[]).includes(i18n.language)
    ? (i18n.language as Locale)
    : DEFAULT_LOCALE;
}

/** Catalogs of a main module and its submodules the person may read; `core` for the kernel. */
export const catalogsQuery = (module: string, locale: Locale) =>
  queryOptions({
    queryKey: ['catalogs', 'list', module, locale],
    queryFn: ({ signal }) =>
      api.get<CatalogSummary[]>(`/catalogs?module=${encodeURIComponent(module)}&locale=${locale}`, {
        signal,
      }),
  });

export const catalogEntriesQuery = (key: string) =>
  queryOptions({
    queryKey: ['catalogs', 'entries', key],
    queryFn: ({ signal }) => api.get<CatalogEntry[]>(`${base(key)}/entries`, { signal }),
  });

/** Changes to catalogs. The server checks the permission of every call. */
export const catalogApi = {
  create: (key: string, input: CatalogEntryInput) =>
    api.post<CatalogEntry>(`${base(key)}/entries`, input),
  update: (key: string, id: string, changes: Partial<CatalogEntryInput> & { version: number }) =>
    api.patch<CatalogEntry>(`${base(key)}/entries/${id}`, changes),
  remove: (key: string, id: string) => api.delete(`${base(key)}/entries/${id}`),
  reorder: (key: string, ids: readonly string[]) =>
    api.put<CatalogEntry[]>(`${base(key)}/order`, { ids }),
  import: (
    key: string,
    input: { entries: readonly unknown[]; deactivateMissing: boolean; dryRun: boolean },
  ) => api.post<CatalogImportResult>(`${base(key)}/import`, input),
  loadDefaults: (input: ({ module: string } | { catalog: string }) & { locale: Locale }) =>
    api.post<CatalogDefaultsResult>('/catalogs/defaults', input),
};

/** Entries of a catalog for forms, lists and badges. */
export interface CatalogData {
  /** All entries in their order, inactive ones included; `undefined` while there are none yet. */
  entries: readonly CatalogEntry[] | undefined;
  /** An entry by ID, inactive ones included. Deleted entries are unknown. */
  entry: (id: string | null | undefined) => CatalogEntry | undefined;
  /** The active entry marked as default. */
  defaultEntry: CatalogEntry | undefined;
  loading: boolean;
  /** Error of the API while there is nothing to show. */
  error: unknown;
  refetch: () => void;
}

const NO_ENTRIES: readonly CatalogEntry[] = [];
const indexes = new WeakMap<readonly CatalogEntry[], ReadonlyMap<string, CatalogEntry>>();

/** Entries by ID, built once per list. */
function indexed(entries: readonly CatalogEntry[]): ReadonlyMap<string, CatalogEntry> {
  let index = indexes.get(entries);
  if (!index) {
    index = new Map(entries.map((entry) => [entry.id, entry]));
    indexes.set(entries, index);
  }
  return index;
}

const compare = (a: string, b: string) => (a < b ? -1 : a > b ? 1 : 0);

/** The order of the API: position, then name, then ID. */
function byOrder(a: CatalogEntry, b: CatalogEntry): number {
  return (
    a.sortOrder - b.sortOrder ||
    compare(a.name.toLowerCase(), b.name.toLowerCase()) ||
    compare(a.id, b.id)
  );
}

/** Entries on the device per catalog, `undefined` while the device has none. */
function useDeviceEntries(): ReadonlyMap<string, readonly CatalogEntry[]> | undefined {
  const { records, available } = useOfflineRecords(CATALOG_OFFLINE_ENTITY);
  return useMemo(() => {
    if (!available) return undefined;
    const catalogs = new Map<string, CatalogEntry[]>();
    for (const record of records) {
      const entry = record.data as unknown as CatalogEntry;
      if (typeof entry.catalog !== 'string') continue;
      const list = catalogs.get(entry.catalog);
      if (list) list.push(entry);
      else catalogs.set(entry.catalog, [entry]);
    }
    for (const list of catalogs.values()) list.sort(byOrder);
    return catalogs;
  }, [records, available]);
}

function catalogData(
  query: UseQueryResult<CatalogEntry[]>,
  device: readonly CatalogEntry[] | undefined,
  online: boolean,
): CatalogData {
  const entries = (online ? query.data : undefined) ?? device ?? query.data;
  const index = entries && indexed(entries);
  return {
    entries,
    entry: (id) => (id ? index?.get(id) : undefined),
    defaultEntry: entries?.find((entry) => entry.isDefault && entry.active),
    loading: !entries && online && query.isPending,
    error: entries ? null : query.error,
    refetch: () => void query.refetch(),
  };
}

/** Entries of several catalogs at once, e.g. those the fields of another catalog name. */
export function useCatalogs(keys: readonly string[]): CatalogData[] {
  const online = useOnline();
  const device = useDeviceEntries();
  const queries = useQueries({
    queries: keys.map((key) => ({ ...catalogEntriesQuery(key), enabled: online })),
  });
  return keys.map((key, index) =>
    catalogData(queries[index]!, device && (device.get(key) ?? NO_ENTRIES), online),
  );
}

/**
 * Entries of a catalog: from the API while online, from the device without network (offline
 * entity `core.catalogs`, docs/architecture/catalogs.md).
 */
export function useCatalog(key: string): CatalogData {
  return useCatalogs([key])[0]!;
}
